import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalysisTools = ({ onToolSelect }) => {
  const [activeCategory, setActiveCategory] = useState('drawing');

  const toolCategories = {
    drawing: {
      label: 'Drawing Tools',
      icon: 'Edit3',
      tools: [
        { id: 'trendline', name: 'Trend Line', icon: 'TrendingUp', description: 'Draw trend lines on chart' },
        { id: 'horizontal', name: 'Horizontal Line', icon: 'Minus', description: 'Support/resistance levels' },
        { id: 'vertical', name: 'Vertical Line', icon: 'Separator', description: 'Time-based markers' },
        { id: 'rectangle', name: 'Rectangle', icon: 'Square', description: 'Price range boxes' },
        { id: 'circle', name: 'Circle', icon: 'Circle', description: 'Circular annotations' },
        { id: 'arrow', name: 'Arrow', icon: 'ArrowRight', description: 'Directional indicators' }
      ]
    },
    fibonacci: {
      label: 'Fibonacci Tools',
      icon: 'GitBranch',
      tools: [
        { id: 'fib_retracement', name: 'Retracement', icon: 'GitBranch', description: 'Fibonacci retracement levels' },
        { id: 'fib_extension', name: 'Extension', icon: 'GitMerge', description: 'Fibonacci extension levels' },
        { id: 'fib_fan', name: 'Fan', icon: 'Fan', description: 'Fibonacci fan lines' },
        { id: 'fib_arc', name: 'Arc', icon: 'Circle', description: 'Fibonacci arc curves' },
        { id: 'fib_time', name: 'Time Zones', icon: 'Clock', description: 'Fibonacci time projections' }
      ]
    },
    gann: {
      label: 'Gann Tools',
      icon: 'Grid3x3',
      tools: [
        { id: 'gann_fan', name: 'Gann Fan', icon: 'Fan', description: 'Gann angle lines' },
        { id: 'gann_box', name: 'Gann Box', icon: 'Square', description: 'Gann square analysis' },
        { id: 'gann_square', name: 'Square of 9', icon: 'Grid3x3', description: 'Gann square of 9' }
      ]
    },
    elliott: {
      label: 'Elliott Wave',
      icon: 'Waves',
      tools: [
        { id: 'elliott_wave', name: 'Wave Count', icon: 'Waves', description: 'Elliott wave labeling' },
        { id: 'elliott_correction', name: 'Correction', icon: 'RotateCcw', description: 'Corrective wave patterns' }
      ]
    },
    measurement: {
      label: 'Measurement',
      icon: 'Ruler',
      tools: [
        { id: 'measure', name: 'Measure', icon: 'Ruler', description: 'Distance and angle measurement' },
        { id: 'price_range', name: 'Price Range', icon: 'BarChart3', description: 'Price range analysis' },
        { id: 'time_cycles', name: 'Time Cycles', icon: 'Clock', description: 'Cyclical time analysis' }
      ]
    }
  };

  const recentAnalyses = [
    {
      id: 1,
      name: 'BTC Support Analysis',
      type: 'Support/Resistance',
      timestamp: '2 hours ago',
      status: 'active'
    },
    {
      id: 2,
      name: 'ETH Fibonacci Setup',
      type: 'Fibonacci Retracement',
      timestamp: '4 hours ago',
      status: 'completed'
    },
    {
      id: 3,
      name: 'Market Structure',
      type: 'Trend Analysis',
      timestamp: '1 day ago',
      status: 'archived'
    }
  ];

  const handleToolSelect = (tool) => {
    onToolSelect(tool);
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Wrench" size={20} />
          <span>Analysis Tools</span>
        </h3>
        <Button variant="ghost" size="sm">
          <Icon name="MoreHorizontal" size={16} />
        </Button>
      </div>
      <div className="p-4">
        {/* Tool Categories */}
        <div className="flex flex-wrap gap-1 mb-4">
          {Object.entries(toolCategories)?.map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                activeCategory === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={category?.icon} size={14} />
              <span className="hidden sm:inline">{category?.label}</span>
            </button>
          ))}
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {toolCategories?.[activeCategory]?.tools?.map((tool) => (
            <button
              key={tool?.id}
              onClick={() => handleToolSelect(tool)}
              className="p-3 text-left border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group"
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name={tool?.icon} size={16} className="text-muted-foreground group-hover:text-primary" />
                <span className="text-sm font-medium text-foreground">{tool?.name}</span>
              </div>
              <p className="text-xs text-muted-foreground">{tool?.description}</p>
            </button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3 mb-6">
          <h4 className="text-sm font-semibold text-foreground">Quick Actions</h4>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" className="justify-start">
              <Icon name="Trash2" size={14} />
              <span className="ml-1">Clear All</span>
            </Button>
            <Button variant="outline" size="sm" className="justify-start">
              <Icon name="Copy" size={14} />
              <span className="ml-1">Duplicate</span>
            </Button>
            <Button variant="outline" size="sm" className="justify-start">
              <Icon name="Download" size={14} />
              <span className="ml-1">Export</span>
            </Button>
            <Button variant="outline" size="sm" className="justify-start">
              <Icon name="Share" size={14} />
              <span className="ml-1">Share</span>
            </Button>
          </div>
        </div>

        {/* Recent Analyses */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Recent Analyses</h4>
          <div className="space-y-2">
            {recentAnalyses?.map((analysis) => (
              <div key={analysis?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{analysis?.name}</span>
                  <div className={`w-2 h-2 rounded-full ${
                    analysis?.status === 'active' ? 'bg-success' :
                    analysis?.status === 'completed' ? 'bg-accent' : 'bg-muted-foreground'
                  }`}></div>
                </div>
                <p className="text-xs text-muted-foreground mb-1">{analysis?.type}</p>
                <p className="text-xs text-muted-foreground">{analysis?.timestamp}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Templates */}
        <div className="mt-6 p-3 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Layout" size={16} className="text-accent" />
            <span className="text-sm font-medium text-foreground">Analysis Templates</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Save and reuse your favorite analysis setups across different charts.
          </p>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" className="flex-1">
              <Icon name="Save" size={12} />
              <span className="ml-1">Save</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-1">
              <Icon name="FolderOpen" size={12} />
              <span className="ml-1">Load</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisTools;